/*
 * Explicit format / minify transforms for munging-expression editors (templated TS: valid TS with
 * `{{...}}` interpolation tokens). Both are user-triggered — we deliberately do NOT format-on-load or
 * compact-on-save, so the stored value always equals what the user sees and entity dirty-state stays honest.
 *
 * Both transforms treat `{{...}}` tokens and string literals as opaque, so whitespace inside them is never
 * touched — the invariant that keeps these from corrupting an expression.
 */

const MASK_TOKEN = /\{\{[\s\S]*?\}\}/g

/** Replace each `{{...}}` with a placeholder identifier so a plain-TS formatter can parse the rest. */
function maskTokens(src: string): { masked: string; tokens: string[] } {
  const tokens: string[] = []
  const masked = src.replace(MASK_TOKEN, (token) => {
    const id = `__MUNGE_${tokens.length}__`
    tokens.push(token)
    return id
  })
  return { masked, tokens }
}

function unmaskTokens(src: string, tokens: string[]): string {
  return src.replace(/__MUNGE_(\d+)__/g, (_match, n) => tokens[Number(n)] ?? _match)
}

/**
 * Collapse runs of whitespace to a single space, but never inside a string literal or a `{{...}}` token
 * — `'A  B'` and `{{ user.name }}` must survive verbatim. A small scanner rather than a regex because
 * only a scanner can tell "whitespace in code" from "whitespace in a string".
 */
export function minifyExpression(src: string): string {
  let out = ''
  let i = 0
  const n = src.length
  while (i < n) {
    const c = src[i]
    if (c === '{' && src[i + 1] === '{') {
      const close = src.indexOf('}}', i + 2)
      const end = close < 0 ? n : close + 2
      out += src.slice(i, end)
      i = end
      continue
    }
    if (c === '"' || c === "'" || c === '`') {
      let j = i + 1
      while (j < n) {
        if (src[j] === '\\') {
          j += 2
          continue
        }
        if (src[j] === c) {
          j++
          break
        }
        j++
      }
      out += src.slice(i, j)
      i = j
      continue
    }
    if (/\s/.test(c)) {
      let j = i
      while (j < n && /\s/.test(src[j])) j++
      out += ' '
      i = j
      continue
    }
    out += c
    i++
  }
  return out.trim()
}

/**
 * Pretty-print a munging expression. Masks `{{...}}` tokens, wraps in a function body so a bare top-level
 * `return` (common in these expressions) parses, runs Prettier, then unwraps and restores the tokens.
 * Prettier is loaded lazily so it stays out of the main bundle. Throws if the expression can't be parsed;
 * callers should treat that as "leave the text unchanged".
 */
export async function formatExpression(src: string): Promise<string> {
  if (src.trim() === '') {
    return src
  }
  const { masked, tokens } = maskTokens(src)
  const [prettier, estree, babel] = await Promise.all([
    import('prettier/standalone'),
    import('prettier/plugins/estree'),
    import('prettier/plugins/babel'),
  ])
  const wrapped = `function __munge__() {\n${masked}\n}`
  const formatted = await prettier.format(wrapped, {
    parser: 'babel',
    plugins: [(estree as any).default ?? estree, (babel as any).default ?? babel],
    singleQuote: true,
    semi: false,
    printWidth: 80,
  })
  // Strip the `function __munge__() {` first line and the closing `}` last line, then de-indent the body.
  const lines = formatted.replace(/\n$/, '').split('\n')
  const body = lines
    .slice(1, -1)
    .map((line) => (line.startsWith('  ') ? line.slice(2) : line))
    .join('\n')
  return unmaskTokens(body, tokens)
}
